import SearchIcon from '@assets/images/icons/search.svg?react'
import type { Placement } from '@popperjs/core/lib/enums'
import cx from 'classnames'
import type React from 'react'
import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { usePopper } from 'react-popper'

import useClickOutside from '../../hooks/useClickOutside'
import { Divider } from './components/Divider'
import { Header } from './components/Header'
import { OptGroup } from './components/OptGroup'
import { Option } from './components/Option'
import optionCss from './components/Option/Option.module.scss'
import { checkForSelectedOption } from './helper/checkForSelectedOption'
import useControls from './hooks/useControls'
import useSelected from './hooks/useSelected'
import css from './Select.module.scss'
import type {
  Options,
  OptionsWithoutGroup,
  Option as OptionType,
} from './types'

export type SelectType = 'input' | 'select'

type Props = {
  type?: SelectType
  placeholder?: string
  label?: string
  selected: string | number | null
  options: Options
  disabled?: boolean
  error?: string | null
  icon?: React.ReactNode
  successIcon?: boolean
  height?: number
  disableScrollIntoView?: boolean
  classNames?: string
  withSearch?: boolean
  position?: Placement
  listMinWidth?: number
  inputClassName?: string
  onChange: (value: string | number) => void
}

const Select: React.FC<Props> = ({
  type = 'input',
  placeholder,
  label,
  selected,
  options,
  disabled,
  error,
  icon,
  successIcon,
  height,
  disableScrollIntoView,
  classNames,
  withSearch,
  position,
  listMinWidth,
  inputClassName,
  onChange,
}) => {
  const [selectedItem, setSelectedItem] = useState<OptionType | null>(null)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [value, setValue] = useState('')
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)
  const [targetRef, setTargetRef] = useState<HTMLElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { isOpen, toggle, close, open } = useControls()

  const clickOutside = useClickOutside<HTMLDivElement>(isOpen, () => {
    close()
    setValue('')
    setFilteredOptions(options)
  })
  const selectedOption = useSelected()

  const { styles, attributes } = usePopper(targetRef, containerRef, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
    placement: position,
  })

  const optionsByCondition = withSearch ? filteredOptions : options

  const isGroup = Array.isArray(optionsByCondition[0])

  useEffect(() => {
    const getSelected = selectedOption({
      options,
      option: selected,
    })

    setSelectedItem(getSelected)
  }, [selected, options])

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && containerRef) {
      const selectedElement = containerRef?.querySelector(
        `.${optionCss.container}[data-selected="true"]`
      )

      if (selectedElement && !disableScrollIntoView) {
        selectedElement.scrollIntoView()
      }
    }
  }, [isOpen])

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (isOpen) {
      const allOptions = containerRef?.querySelectorAll(
        `.${optionCss.container}`
      )

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (allOptions) {
          const nextOption = Array.from(allOptions).find(
            (option) => option.getAttribute('data-selected') === 'true'
          )?.nextElementSibling as HTMLElement

          if (nextOption) {
            const currentOption = Array.from(allOptions).find(
              (option) => option.getAttribute('data-selected') === 'true'
            ) as HTMLElement

            nextOption.classList.add(optionCss.selected)

            nextOption.setAttribute('data-selected', 'true')

            if (currentOption) {
              currentOption.classList.remove(optionCss.selected)
              currentOption.setAttribute('data-selected', 'false')
            }

            nextOption.scrollIntoView()
          }
        }
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (allOptions) {
          const prevOption = Array.from(allOptions).find(
            (option) => option.getAttribute('data-selected') === 'true'
          )?.previousElementSibling as HTMLElement

          if (prevOption) {
            const currentOption = Array.from(allOptions).find(
              (option) => option.getAttribute('data-selected') === 'true'
            ) as HTMLElement

            prevOption.classList.add(optionCss.selected)

            prevOption.setAttribute('data-selected', 'true')

            if (currentOption) {
              currentOption.classList.remove(optionCss.selected)
              currentOption.setAttribute('data-selected', 'false')
            }

            prevOption.scrollIntoView()
          }
        }
      }

      if (!withSearch) {
        if (e.key.length === 1) {
          e.preventDefault()
          if (allOptions) {
            const selectedOptionIndex = Array.from(allOptions).findIndex(
              (option) => option.getAttribute('data-selected') === 'true'
            )

            const nextOption = Array.from(allOptions).find(
              (option, index) =>
                option.textContent?.toLowerCase().startsWith(e.key) &&
                index > selectedOptionIndex
            ) as HTMLElement

            if (nextOption) {
              const currentOption = Array.from(allOptions).find(
                (option) => option.getAttribute('data-selected') === 'true'
              ) as HTMLElement

              nextOption.classList.add(optionCss.selected)

              nextOption.setAttribute('data-selected', 'true')

              if (currentOption) {
                currentOption.classList.remove(optionCss.selected)
                currentOption.setAttribute('data-selected', 'false')
              }

              nextOption.scrollIntoView()
            } else {
              const nextOption = Array.from(allOptions).find((option) =>
                option.textContent?.toLowerCase().startsWith(e.key)
              ) as HTMLElement

              if (nextOption) {
                const currentOption = Array.from(allOptions).find(
                  (option) => option.getAttribute('data-selected') === 'true'
                ) as HTMLElement

                nextOption.classList.add(optionCss.selected)

                nextOption.setAttribute('data-selected', 'true')

                if (currentOption) {
                  currentOption.classList.remove(optionCss.selected)
                  currentOption.setAttribute('data-selected', 'false')
                }

                nextOption.scrollIntoView()
              }
            }
          }
        }
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (allOptions) {
          const selectedOption = Array.from(allOptions).find(
            (option) => option.getAttribute('data-selected') === 'true'
          ) as HTMLElement

          if (selectedOption) {
            const value = selectedOption.getAttribute('data-value')

            if (value) {
              onChange(value)
              close()
              setValue('')
              setFilteredOptions(options)
            }
          }
        }
      }

      if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault()

        close()
        setValue('')
        setFilteredOptions(options)
      }
    }

    if (!isOpen) {
      if (e.key === 'Enter') {
        e.preventDefault()

        open()
      }
    }
  }

  const handleOnClick = (value: string | number) => {
    close()

    onChange(value)
    setValue('')
    setFilteredOptions(options)
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    const query = e.target.value

    const updatedList = [...options] as OptionType[]

    if (query) {
      setFilteredOptions(
        updatedList.filter((item) => {
          return item.label.toLowerCase().startsWith(query.toLowerCase())
        })
      )
    } else {
      setFilteredOptions(options)
    }

    setValue(query)
  }

  const checkIsSelected = () => {
    if (!isGroup) {
      const options = optionsByCondition as OptionType[]

      return checkForSelectedOption(options, selectedItem?.value)
    }
  }

  const maxHeight = withSearch
    ? `calc(${height || 400}px - 52px)`
    : height || 400

  return (
    <div
      className={cx(css.container, classNames)}
      onKeyDown={handleKeyDown}
      {...clickOutside}
    >
      <div className={css.body}>
        <Header
          active={isOpen}
          type={type}
          error={error}
          placeholder={placeholder}
          label={label}
          onClick={!disabled ? toggle : () => {}}
          disabled={disabled}
          selected={selectedItem}
          icon={icon}
          isOpen={isOpen}
          inputClassName={inputClassName}
          setTargetRef={setTargetRef}
        />

        {isOpen && (
          <div
            ref={setContainerRef}
            className={cx(css.listWrap, {
              [css.fullWidth]: type === 'input',
              [css.hasLabel]: label,
            })}
            style={{
              maxHeight: height || 400,
              minWidth: listMinWidth || 160,
              ...styles.popper,
            }}
            {...attributes.popper}
          >
            {withSearch && (
              <div className={css.inputWrap}>
                <input
                  ref={inputRef}
                  type="text"
                  className={cx(css.input)}
                  value={value}
                  onChange={handleSearch}
                  placeholder="Search..."
                />
                <span className={css.searchIcon}>
                  <SearchIcon />
                </span>
              </div>
            )}

            <div className={css.list} style={{ maxHeight }}>
              {isGroup &&
                optionsByCondition.map((group, key) => (
                  <div key={key}>
                    <OptGroup
                      options={group as OptionsWithoutGroup}
                      onClick={handleOnClick}
                      selectedValue={selectedItem?.value}
                    />

                    {key !== optionsByCondition.length - 1 && <Divider />}
                  </div>
                ))}

              {!isGroup &&
                optionsByCondition.map((option: any, index) => (
                  <Option
                    {...option}
                    key={option.value}
                    successIcon={
                      successIcon && option.value === selectedItem?.value
                    }
                    onClick={handleOnClick}
                    data-selected={
                      option.value === selectedItem?.value ||
                      (!checkIsSelected() && index === 0)
                    }
                    data-value={option.value}
                    selected={
                      option.value === selectedItem?.value ||
                      (!checkIsSelected() && index === 0)
                    }
                  >
                    {option.label}
                  </Option>
                ))}

              {optionsByCondition.length === 0 && (
                <div className={css.notFoundText}>Nothing found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <div className={css.error}>{error}</div>}
    </div>
  )
}

export default Select
